// Libraries
import React from "react";

import { useResolvePendingItemMutation, useGetPendingItemsQuery, useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { debounce } from "lodash";
import { Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { OnChange } from "react-final-form-listeners";

// Components
import { Table, TR, TD, TH } from "Cobalt/TableV2";
import { SIFinalFormFieldNoCol, SIFormSelect, SIFormInput } from "Common/forms";
import InfoPopover from "Common/InfoPopover";
import { CompleteStatusBadge, IncompleteStatusBadge } from "Common/StatusBadge";
// will probably want BaseStatusBadge, CompleteStatusBadge, WarningIcon,

// Utilities
import makeBem from "Utils/makeBem";

// Component Helpers
const bem = makeBem("WageReportValidationTable");
const focusOnErrors = createDecorator();

/**
 * Use the missing_from_app issues to create a list of names for resolve missing_from_doc
 * issues that are "listed under a different name."
 */
const getWageReportIssueNameOptions = (pendingItems) => {
  const wageReportPendingItem = pendingItems.find((pendingItem) => pendingItem.type === "wage_report_issue");
  if (!wageReportPendingItem) return null;

  const missingFromAppIssues = wageReportPendingItem.payload.filter((issue) => issue.validation_issue_type === "missing_from_app");

  if (missingFromAppIssues.length === 0) {
    return null;
  }

  // Format employee names to be used as SIFormSelect options
  const nameOptions = missingFromAppIssues.map((issue) => [issue.employee_name, issue.employee_name]);

  nameOptions.push(["None of the above", "None of the above"]);
  return nameOptions;
};

// Components

const AdditionalInformationField = ({ fieldRef, resolution, alternativeNameOptions, requiresDocumentation }) => {
  if (resolution === "other") {
    return (
      <SIFinalFormFieldNoCol
        name={`${fieldRef}.resolution_explanation`}
        component={SIFormInput}
        componentProps={{
          placeholder: "Please explain",
        }}
        sm={12}
        pl={0}
        mb={0}
        compact
      />
    );
  }
  if (resolution === "different_name") {
    if (alternativeNameOptions) {
      return (
        <SIFinalFormFieldNoCol
          name={`${fieldRef}.resolution_explanation`}
          component={SIFormSelect}
          componentProps={{
            options: alternativeNameOptions,
            placeholder: "Select an option",
          }}
          sm={12}
          pl={0}
          mb={0}
          compact
        />
      );
    }
    return (
      <SIFinalFormFieldNoCol
        name={`${fieldRef}.resolution_explanation`}
        component={SIFormInput}
        componentProps={{
          placeholder: "Enter employee name",
        }}
        sm={12}
        pl={0}
        mb={0}
        compact
      />
    );
  }
  if (requiresDocumentation) {
    return (
      <>
        Submit Documentation
        <InfoPopover infoText="The Insurance Carriers will require documentation to support this. Upload it in the Upload Documents section above." />
      </>
    );
  }
  return null;
};

const WageReportIssues = () => {
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const companyPolicyId = companyApplication?.id;
  const { data: pendingItemsRaw } = useGetPendingItemsQuery(companyPolicyId, { skip: !companyPolicyId });
  const pendingItems = pendingItemsRaw?.pending_items ?? [];
  const alternativeNameOptions = getWageReportIssueNameOptions(pendingItems);
  const wageReportPendingItem = pendingItems.find((pendingItem) => pendingItem.type === "wage_report_issue");

  const wageReportIssues = wageReportPendingItem
    ? wageReportPendingItem.payload.filter(
        // Currently we only have the UI for handling missing_from_doc issues.
        // Will remove this filter after we add other issue types to the frontend.
        (issue) => issue.validation_issue_type === "missing_from_doc",
      )
    : [];

  const [resolvePendingItem] = useResolvePendingItemMutation();

  const issueHandler = async (values) => {
    const { id: document_validation_issue_id, resolution, resolution_explanation } = values;

    await resolvePendingItem({
      companyPolicyId,
      type: "wage_report_issue",
      resolution_type: "update_wage_report_issue",
      document_validation_issue_id,
      resolution,
      resolution_explanation,
    });
  };

  /**
   * Why debounce: We don't want to send a request every time a user updates
   * the text field, and instead want them to finish typing.
   *
   * How does the debounce work: This is a simple example of the debounce() function.
   * ```
   * const debounce = (fn, delay) => {
   *   let timer;
   *   return () => {
   *     if(timer) clearTimeout(timer);
   *     timer = setTimeout(fn, delay)
   *   }
   * }
   * ```
   *
   * We only call debounce() once, and use the function it returns in the rest of
   * the code. This second function keeps track of the delay timer in its closure,
   * and each function call resets the delay.
   */

  const delayedIssueHandler = debounce((employeeValues) => issueHandler(employeeValues), 2000);

  const submitHandler = () => {};
  return (
    <Form
      onSubmit={submitHandler}
      mutators={{
        ...arrayMutators,
      }}
      decorators={[focusOnErrors]}
      initialValues={{
        wageReportIssues,
      }}
      keepDirtyOnReinitialize
    >
      {({ handleSubmit, values, form }) => (
        <form onSubmit={handleSubmit}>
          <FieldArray name="wageReportIssues">
            {({ fields }) => (
              <Table className={bem()} mb={0}>
                <thead>
                  <TR>
                    <TH>Employee Name</TH>
                    <TH>Explanation</TH>
                    <TH className="flex-col-4">Additional information</TH>
                    <TH className="flex-col-2">Status</TH>
                  </TR>
                </thead>
                <tbody>
                  {fields.map((name, index) => (
                    <TR key={index}>
                      <TD>
                        {wageReportIssues[index].employee_name}
                        <InfoPopover
                          infoText={`${wageReportIssues[index].employee_name} was listed on your application but not found on the documentation you submitted.`}
                        />
                      </TD>
                      <TD>
                        <SIFinalFormFieldNoCol
                          name={`${name}.resolution`}
                          component={SIFormSelect}
                          componentProps={{
                            options: {
                              Owner: "owner",
                              "1099 Contractor": "1099_contractor",
                              "Recently Hired Employee": "new_hire",
                              "Listed Under a Different Name": "different_name",
                              Other: "other",
                            },

                            placeholder: "Select an option",
                          }}
                          compact
                          onChange={() => issueHandler(values)}
                        />

                        <OnChange name={`${name}.resolution`}>
                          {() => {
                            form.change(`${name}.resolution_explanation`, undefined);
                            const employeeValues = values.wageReportIssues[index];
                            issueHandler(employeeValues);
                          }}
                        </OnChange>
                      </TD>
                      <TD>
                        <AdditionalInformationField
                          fieldRef={name}
                          resolution={values.wageReportIssues[index].resolution}
                          alternativeNameOptions={alternativeNameOptions}
                          requiresDocumentation={values.wageReportIssues[index].requires_documentation}
                        />

                        <OnChange name={`${name}.resolution_explanation`}>
                          {() => {
                            const employeeValues = values.wageReportIssues[index];

                            delayedIssueHandler(employeeValues);
                          }}
                        </OnChange>
                      </TD>
                      <TD>
                        {/* This uses the RTK value so it accurately reflects the "status" of item.  This form intakes the entire wage report issue object, but only a couple fields are form controlled, so you can't rely on the form values for non-controller fields. */}
                        {wageReportIssues[index].is_resolved ? <CompleteStatusBadge /> : <IncompleteStatusBadge />}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            )}
          </FieldArray>
        </form>
      )}
    </Form>
  );
};

export default WageReportIssues;
